import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { config } from "../Environment";
import useRetryFetch from "../hooks/useRetryFetch";
import { UserProfile } from "./types";

export function cn(...inputs: any) {
  return twMerge(clsx(inputs));
}

/**
 * Get display name for a user with proper fallback logic
 * Priority: displayName > firstName + lastName > email username
 */
export function getDisplayName(user: UserProfile | any): string {
  if (!user) return "A user";

  // 1. Use displayName if available
  if (typeof user.displayName === "string" && user.displayName.trim().length > 0) {
    return user.displayName;
  }

  // 2. Fall back to firstName + lastName
  if (user.firstName || user.lastName) {
    const parts = [user.firstName, user.lastName].filter(Boolean);
    return parts.join(" ");
  }

  // 3. Fall back to email username
  if (user.email) {
    return user.email.split("@")[0];
  }

  return "A user";
}

export const LANGUAGES: any = {
  en: { label: "english", dir: "ltr" },
  de: { label: "german", dir: "ltr" },
  he: { label: "hebrew", dir: "rtl" },
};

/** Resolve text direction from language code (e.g. "he", "he-IL" -> "rtl"). */
export function getDirForLanguage(lang: string | undefined): "ltr" | "rtl" {
  if (!lang) return "ltr";
  const dir = LANGUAGES[lang]?.dir ?? LANGUAGES[lang?.split?.("-")[0]]?.dir;
  return dir === "rtl" ? "rtl" : "ltr";
}

export const convertOldCustomToNew = (oldCustomData: any) => {
  let result = [];
  if (oldCustomData["richText"]) {
    let newid = "richtext-" + Math.floor(Math.random() * 10000);
    result.push({
      id: newid,
      type: "richtext",
      content: oldCustomData["richText"],
    });
  }

  if (oldCustomData["checkboxes"] && oldCustomData["checkboxes"].length) {
    let newid = "checkbox-" + Math.floor(Math.random() * 10000);
    result.push({
      id: newid,
      type: "checkbox",
      content: {
        label: "",
        key: newid,
        options: oldCustomData["checkboxes"].map((option: any) => {
          return {
            label: option.label,
            key: option.id,
          };
        }),
      },
    });
  }

  if (oldCustomData["twoDfeedback"]) {
    let newid = "gauge-" + Math.floor(Math.random() * 10000);
    result.push({
      id: newid,
      type: "gauge",
      content: { key: newid },
    });
  }

  return result;
};

export interface DiagramDataOption {
  text: { [lang: string]: string };
  value: string;
  handle: string;
}

export interface DiagramNode {
  label: { [lang: string]: string };
  type: string;
  historyId: string;
  options: DiagramDataOption[];
  dataitemmapping?: any;
  custom?: any;
}

export interface DiagramEdge {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  markerEnd: {
    type: string;
    color: string;
    width: number;
    height: number;
  };
  type: string;
}

export interface Diagram {
  prescriptionLanguage?: string;
  version?: number;
  nodes?: any[];
  edges: DiagramEdge[];
  data: { [nodeId: string]: DiagramNode };
  [key: string]: any;
}

/**
 * Adds a nested prescription node and links it to a parent node in the diagram.
/**
 * Hook to link a nested prescription node to a parent node in the diagram.
 */
export function useLinkNestedDiagram() {
  const retryFetch = useRetryFetch();
  return async ({
    diagram,
    fromNodeId,
    nestedPrescId,
    nestedLabel = "Nested Activity",
    nestedPrescName = "",
    addReverseEdge = false, // <--- new parameter
    reverseTargetId = fromNodeId, // <--- can pick another node if needed
  }: {
    diagram: Diagram;
    fromNodeId: string;
    nestedPrescId: string;
    nestedLabel?: string;
    nestedPrescName?: string; // optionally supply to tag flowcontext
    addReverseEdge?: boolean;
    reverseTargetId?: string;
  }): Promise<Diagram> => {
    const newId = Math.floor(Math.random() * 9999);
    const newNodeId = `nested_${newId}`;
    const newHandle = `${fromNodeId}-quicknested-${newId}`;

    // Deep clone
    const updatedDiagram: Diagram = JSON.parse(JSON.stringify(diagram));

    // 1. Fetch the full nested prescription, including diagramJSON, contract, openExits, etc.
    // Simulate what DesignView does: assume we get an object with .diagramJSON, .name, .contract, etc.
    let nestedPrescResp = await retryFetch(
      `${config.API_BASE}/api/prescriptions/${nestedPrescId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );
    let nestedPrescObj = await nestedPrescResp.json();

    // Parse the inner diagram
    let diagramObj =
      typeof nestedPrescObj.diagramJSON === "string"
        ? JSON.parse(nestedPrescObj.diagramJSON)
        : nestedPrescObj.diagramJSON;

    // 2. Create a new version in history if needed (as done in DesignView)
    let presHistoryId = nestedPrescObj.history;
    if (!presHistoryId) {
      let response = await retryFetch(
        `${config.API_BASE}/api/prescriptions/newversion/${nestedPrescObj._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: "{}",
        }
      );
      let responseJSON = await response.json();
      presHistoryId = responseJSON.newHistoryId;
    }

    // 3. Prepare exit options from openExits of the nested prescription
    let exitOptions: any[] = [];
    let optIdx = 0;
    for (let openExit of diagramObj.openExits || []) {
      exitOptions.push({
        text: {
          en: `${openExit.nodeLabel} [${openExit.optionLabel}]`,
        },
        value: openExit.optionValue || "?????",
        innerHandle: openExit.handleId,
        handle: `${newNodeId}-${optIdx}`,
      });
      optIdx++;
    }

    // 4. Set up dataitemmapping from contract if exists, else as empty array
    let dataitemmapping =
      Array.isArray(diagramObj.contract) && diagramObj.contract.length
        ? diagramObj.contract.map((variable: any) => ({
            key: variable.value,
            description: variable.label,
            type: variable.type || "text",
            mappedToOuter: { key: "", description: "" },
          }))
        : [];

    // 5. Add the new node to diagram.data
    updatedDiagram.data[newNodeId] = {
      label: {
        en: nestedPrescObj.name || nestedLabel,
        de: nestedPrescObj.name || nestedLabel,
        he: nestedPrescObj.name || nestedLabel,
      },
      type: "prescription",
      historyId: presHistoryId,
      dataitemmapping,
      options: exitOptions,
    };

    // 6. Add node to nodes array for React Flow
    updatedDiagram.nodes = updatedDiagram.nodes || [];
    updatedDiagram.nodes.push({
      id: newNodeId,
      type: "activity",
      position: {
        x: 200 + Math.floor(Math.random() * 100),
        y: 200 + Math.floor(Math.random() * 100),
      },
    });

    // 7. Add edge from parent to nested node
    updatedDiagram.edges = updatedDiagram.edges || [];
    updatedDiagram.edges.push({
      id: `${fromNodeId}-${newNodeId}`,
      source: fromNodeId,
      sourceHandle: newHandle,
      target: newNodeId,
      targetHandle: `${newNodeId}-targetSideLeft`,
      markerEnd: {
        type: "arrowclosed",
        color: "black",
        width: 20,
        height: 20,
      },
      type: "customedge",
    });
    // Optionally add a reverse connection from the nested node's exit(s) back to the parent
    if (addReverseEdge && exitOptions.length) {
      for (const opt of exitOptions) {
        updatedDiagram.edges.push({
          id: `${newNodeId}-${reverseTargetId}`,
          source: newNodeId,
          sourceHandle: opt.handle, // the handle of the nested node's exit
          target: reverseTargetId,
          targetHandle: `${reverseTargetId}-targetSideLeft`,
          markerEnd: {
            type: "arrowclosed",
            color: "black",
            width: 20,
            height: 20,
          },
          type: "customedge",
        });
      }
    }

    // 8. Add an option to parent node to link to the nested activity
    const parentOptions = updatedDiagram.data[fromNodeId].options || [];
    const parentOptionValue = `nested_${nestedPrescId}`;
    const foundIdx = parentOptions.findIndex(
      (opt) => opt.value === parentOptionValue && opt.handle === newHandle
    );

    const newOption = {
      text: { en: nestedPrescObj.name || nestedLabel },
      value: parentOptionValue,
      handle: newHandle,
      nextActivities: [newNodeId], // <-- critical for correct routing!
    };

    if (foundIdx >= 0) {
      parentOptions[foundIdx] = newOption; // update in place
    } else {
      parentOptions.push(newOption);
    }
    updatedDiagram.data[fromNodeId].options = parentOptions;

    // 9. Merge the nested flowcontext into the parent’s, appending the nested activity's name in the description (DesignView behavior)
    if (diagramObj.flowcontext) {
      updatedDiagram.flowcontext = updatedDiagram.flowcontext || [];
      for (let newEntry of diagramObj.flowcontext) {
        // correct description to include nested prescription name
        updatedDiagram.flowcontext.push({
          ...newEntry,
          description: `${newEntry.description} (${
            nestedPrescObj.name || nestedLabel
          })`,
        });
      }
    }

    return updatedDiagram;
  };
}
